import React, { useMemo } from 'react';

/**
 * Enhanced Avatar component with customizable size, fallback image support,
 * and optimized color generation for consistent, accessible colors.
 */
const Avatar = ({ 
  firstName = '', 
  lastName = '', 
  size = 'md', 
  className = '',
  fallbackImage,
  status,
  statusPosition = 'bottom-right',
  background,
  textColor,
  border = false,
  onClick
}) => {
  // Size mapping for consistent dimensions
  const sizeMap = {
    xs: { width: '24px', height: '24px', fontSize: '10px' },
    sm: { width: '32px', height: '32px', fontSize: '12px' },
    md: { width: '40px', height: '40px', fontSize: '14px' },
    lg: { width: '48px', height: '48px', fontSize: '16px' },
    xl: { width: '64px', height: '64px', fontSize: '20px' },
    '2xl': { width: '80px', height: '80px', fontSize: '24px' }
  };

  // Status indicator styles
  const statusColorMap = {
    online: '#10b981', // green
    away: '#f59e0b',   // amber
    busy: '#ef4444',   // red
    offline: '#6b7280' // gray
  };

  // Enhanced color generation algorithm for better contrast and consistency
  const generateAvatarColor = (name) => {
    if (!name) return { background: '#6b7280', color: '#ffffff' };
    
    // Use a more sophisticated hash function for better distribution
    let hash = 0;
    const normalizedName = name.toLowerCase();
    
    for (let i = 0; i < normalizedName.length; i++) {
      hash = ((hash << 5) - hash) + normalizedName.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    
    // Generate colors from a predefined palette for better aesthetics
    const colorPalette = [
      { background: '#f87171', color: '#ffffff' }, // red
      { background: '#60a5fa', color: '#ffffff' }, // blue
      { background: '#34d399', color: '#ffffff' }, // green
      { background: '#a78bfa', color: '#ffffff' }, // purple
      { background: '#fbbf24', color: '#ffffff' }, // yellow
      { background: '#f472b6', color: '#ffffff' }, // pink
      { background: '#2dd4bf', color: '#ffffff' }, // teal
      { background: '#fb923c', color: '#ffffff' }, // orange
      { background: '#4b5563', color: '#ffffff' }, // gray
      { background: '#818cf8', color: '#ffffff' }, // indigo
    ];
    
    // Ensure consistent color for the same name
    const index = Math.abs(hash) % colorPalette.length;
    return colorPalette[index];
  };

  // Get initials, handling edge cases
  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '?';
    
    // Handle single name case
    if (!firstName || !lastName) {
      const singleName = (firstName || lastName).trim();
      if (!singleName) return '?';
      
      // For single long names, use first and last character
      if (singleName.length > 5) {
        return `${singleName[0]}${singleName[singleName.length - 1]}`.toUpperCase();
      }
      
      // For shorter names, use first character only
      return singleName[0].toUpperCase();
    }
    
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  };

  // Memoize the generated values to avoid recalculation on every render
  const initials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName]);
  const avatarColors = useMemo(() => 
    generateAvatarColor(`${firstName} ${lastName}`),
    [firstName, lastName]
  );

  // Determine dimensions based on size prop
  const dimensions = sizeMap[size] || sizeMap.md;
  
  // Status indicator positioning
  const statusPositionStyles = {
    'top-right': { top: '0', right: '0', transform: 'translate(25%, -25%)' },
    'top-left': { top: '0', left: '0', transform: 'translate(-25%, -25%)' },
    'bottom-right': { bottom: '0', right: '0', transform: 'translate(25%, 25%)' },
    'bottom-left': { bottom: '0', left: '0', transform: 'translate(-25%, 25%)' }
  };

  // Handle click event
  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  // Render avatar with image if provided
  if (fallbackImage) {
    return (
      <div 
        className={`relative inline-flex ${onClick ? 'cursor-pointer' : ''} ${className}`}
        style={{ width: dimensions.width, height: dimensions.height }}
        onClick={handleClick}
      >
        <img 
          src={fallbackImage} 
          alt={`${firstName} ${lastName}`} 
          className="rounded-full object-cover w-full h-full"
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div
          className="rounded-full absolute inset-0 items-center justify-center font-medium"
          style={{
            backgroundColor: background || avatarColors.background,
            color: textColor || avatarColors.color,
            fontSize: dimensions.fontSize,
            display: 'none',
            border: border ? '2px solid white' : 'none',
            boxShadow: border ? '0 0 0 1px rgba(0, 0, 0, 0.05)' : 'none'
          }}
        >
          {initials}
        </div>
        
        {status && (
          <span 
            className="absolute block rounded-full border-2 border-white"
            style={{
              width: `${parseInt(dimensions.width) / 3}px`,
              height: `${parseInt(dimensions.height) / 3}px`,
              backgroundColor: statusColorMap[status] || status,
              ...statusPositionStyles[statusPosition]
            }}
          />
        )}
      </div>
    );
  }

  // Render avatar with initials
  return (
    <div 
      className={` inline-flex ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={handleClick}
    >
      <div
        className="rounded-full flex items-center justify-center font-medium"
        style={{
          backgroundColor: background || avatarColors.background,
          color: textColor || avatarColors.color,
          width: dimensions.width,
          height: dimensions.height,
          fontSize: dimensions.fontSize,
          border: border ? '2px solid white' : 'none',
          boxShadow: border ? '0 0 0 1px rgba(0, 0, 0, 0.05)' : 'none'
        }}
      >
        {initials}
      </div>
      
      {status && (
        <span 
          className="absolute block rounded-full border-2 border-white"
          style={{
            width: `${parseInt(dimensions.width) / 3}px`,
            height: `${parseInt(dimensions.height) / 3}px`,
            backgroundColor: statusColorMap[status] || status,
            ...statusPositionStyles[statusPosition]
          }}
        />
      )}
    </div>
  );
};

export default Avatar;